/**
 * Workflow Action Registry
 * Maps each WorkflowAction variant to { label, effect }.
 *
 * The backend never encodes routes (ADR-JO-007); the frontend owns URL/modal construction.
 */

import type { WorkflowAction } from '../types/workflow';

export type ActionEffect =
  | { kind: 'navigate'; url: string }
  | { kind: 'openModal'; modalKey: string; params: Record<string, string> };

export interface ActionDescriptor {
  label: string;
  effect: ActionEffect;
}

/**
 * Resolve a WorkflowAction variant to a { label, effect } descriptor.
 * All URL construction lives here — never in components.
 */
export function resolveAction(action: WorkflowAction): ActionDescriptor {
  switch (action.type) {
    case 'choose_application':
      return {
        label: 'Choose application',
        effect: {
          kind: 'openModal',
          modalKey: 'choose-application',
          params: { unitId: action.refs.unitId },
        },
      };

    case 'add_tenants':
      return {
        label: 'Add tenants',
        effect: {
          kind: 'openModal',
          modalKey: 'review-tenants',
          params: { unitId: action.refs.unitId },
        },
      };

    case 'set_prior_lease_end':
      return {
        label: 'Set lease end date',
        effect: {
          kind: 'navigate',
          url: `/leases/${action.refs.priorLeaseId}`,
        },
      };

    case 'set_disclosures':
      return {
        label: 'Configure disclosures',
        effect: {
          kind: 'navigate',
          url: `/units/${action.refs.unitId}/disclosures`,
        },
      };

    case 'setup_bank_account':
      // actionPath is resolver-picked: '/settings/ach-accounts' or '/verification'
      return {
        label: 'Set up bank account',
        effect: {
          kind: 'navigate',
          url: action.refs.actionPath,
        },
      };

    case 'create_lease':
      return {
        label: 'Create lease',
        effect: {
          kind: 'openModal',
          modalKey: 'byo-vs-wizard',
          params: { unitId: action.refs.unitId },
        },
      };

    case 'setup_payments': {
      const step = action.refs.step;

      // The verified-bank step is configured on the GLOBAL ACH settings page (Stripe Connect
      // onboarding + funding sources), not on the lease's payments tab. Deep-link there.
      if (step === 'verified_bank_account') {
        return {
          label: 'Connect bank account',
          effect: { kind: 'navigate', url: '/settings/ach-accounts' },
        };
      }

      // The remaining steps all live on the lease's payments tab, but in DISTINCT sections.
      // Each carries a `?focus=` so the page scrolls to and highlights the relevant section.
      // The #payments hash is REQUIRED — LeaseDetailPage reads the active tab from
      // window.location.hash; without it the page loads on the Overview tab (step-4 bug).
      const STEP_CONFIG: Record<string, { label: string; section: string }> = {
        deposit_account: { label: 'Select deposit account', section: 'deposit' },
        payment_terms: { label: 'Set payment terms', section: 'terms' },
        late_fees: { label: 'Configure late fees', section: 'late-fees' },
        monitoring_account: { label: 'Connect monitoring account', section: 'monitoring' },
        // Terminal action once a method is chosen and its steps are done: deep-link to the
        // activation card so the landlord can flip payments on.
        activate: { label: 'Activate payments', section: 'activate' },
      };
      const cfg = step ? STEP_CONFIG[step] : undefined;
      const url = cfg
        ? `/leases/${action.refs.leaseId}?focus=${cfg.section}#payments`
        : `/leases/${action.refs.leaseId}#payments`;
      return {
        label: cfg?.label ?? 'Set up payments',
        effect: { kind: 'navigate', url },
      };
    }

    case 'record_move_in_walkthrough':
      return {
        label: 'Record move-in walkthrough',
        effect: {
          kind: 'navigate',
          url: `/leases/${action.refs.leaseId}/condition-walkthroughs/move-in`,
        },
      };

    case 'record_move_out_walkthrough':
      return {
        label: 'Record move-out walkthrough',
        effect: {
          kind: 'navigate',
          url: `/leases/${action.refs.leaseId}/condition-walkthroughs/move-out`,
        },
      };
  }
}
